import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { getReferenceString } from '@medplum/core';
import { OperationOutcome, Patient, RelatedPerson, Resource } from '@medplum/fhirtypes';
import { useCallback, useMemo, useState } from 'react';
import { PATIENT_RELATED_PERSONS_QUERY, RelatedPersonFragment } from '../graphql';

export interface UseRelatedPersonListResult {
  relatedPersonList: RelatedPerson[];
  relatedPersonListLoading: boolean;
  relatedPersonListError: OperationOutcome | Error | undefined;
  refreshRelatedPersonList: () => void;
  pauseLiveUpdates: () => void;
}

export function useRelatedPersonList(patient: Patient | undefined): UseRelatedPersonListResult {
  const [areLiveUpdatesPaused, setAreLiveUpdatesPaused] = useState(false);

  const queryVariables = useMemo(() => {
    if (!patient) return undefined;
    return {
      patientRef: getReferenceString(patient),
    };
  }, [patient]);

  const { data, loading, error, refresh } = useLiveGraphQL<RelatedPersonFragment & Resource>(
    PATIENT_RELATED_PERSONS_QUERY,
    queryVariables,
    {
      pauseLiveUpdates: areLiveUpdatesPaused,
    }
  );

  const pauseLiveUpdates = useCallback(() => {
    setAreLiveUpdatesPaused(true);
  }, []);

  const refreshRelatedPersonList = useCallback(() => {
    setAreLiveUpdatesPaused(false);
    refresh();
  }, [refresh]);

  return {
    relatedPersonList: data as RelatedPerson[],
    relatedPersonListLoading: loading,
    relatedPersonListError: error,
    refreshRelatedPersonList,
    pauseLiveUpdates,
  };
}
